import { FadeIn } from "./FadeIn";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from "recharts";

const WIDGETS = [
  { label: "New Enquiries", value: "7", note: "3 today" },
  { label: "Quotes Requiring Attention", value: "4", note: "2 complex jobs", accent: true },
  { label: "Accepted Jobs", value: "12", note: "awaiting scheduling" },
  { label: "Active Jobs", value: "23", note: "across 6 employees" },
  { label: "Jobs Delayed", value: "3", note: "materials pending", accent: true },
  { label: "Ready for Collection", value: "6", note: "customers notified" },
  { label: "Employee Activity", value: "8", note: "currently on jobs" },
  { label: "Low Stock", value: "5", note: "items below threshold", accent: true },
  { label: "Purchase Orders", value: "9", note: "open with suppliers" },
  { label: "Outstanding Invoices", value: "14", note: "synced to accounting" },
  { label: "Major Contracts", value: "3", note: "milestones on track" },
];

const CHART_DATA = [
  { w: "W1", jobs: 18 }, { w: "W2", jobs: 22 }, { w: "W3", jobs: 19 },
  { w: "W4", jobs: 26 }, { w: "W5", jobs: 24 }, { w: "W6", jobs: 29 },
];

const Widget = ({ label, value, note, accent }) => (
  <div className="border border-[#2A2D2A] rounded-md p-4 bg-[#181A18]" data-testid={`dashboard-widget-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 leading-tight">{label}</p>
    <p className={`mt-2 font-heading font-bold text-2xl ${accent ? "text-sage-light" : "text-white"}`}>{value}</p>
    <p className="mt-1 text-[11px] text-zinc-500">{note}</p>
  </div>
);

export const DashboardMockup = () => (
  <section className="bg-charcoal" data-testid="dashboard-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-sage uppercase mb-5">Management Visibility</p>
        <h2 className="font-heading font-bold tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="dashboard-heading">
          One place to understand what is happening across your business.
        </h2>
      </FadeIn>
      <FadeIn delay={0.12} className="mt-14">
        <div className="rounded-lg border border-[#2A2D2A] bg-[#141614] overflow-hidden shadow-2xl" data-testid="dashboard-mockup">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2A2D2A] bg-[#101210]">
            <div className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#3A3D3A]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#3A3D3A]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#3A3D3A]" />
            </div>
            <div className="flex-1 max-w-md mx-auto bg-[#1C1E1C] rounded px-3 py-1 text-center text-[11px] text-zinc-500 tracking-wide">
              yourbusiness.westlinetechnologies.com — demonstration data only
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="font-heading font-bold text-white text-sm tracking-[0.18em]">WESTLINE OS</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Operations overview — demo business</p>
              </div>
              <span className="hidden sm:inline-block text-[10px] font-semibold tracking-[0.18em] uppercase text-sage-light border border-[#2A463A] rounded px-2.5 py-1">
                Fictional demo data
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {WIDGETS.slice(0, 6).map((w) => <Widget key={w.label} {...w} />)}
              <div className="col-span-2 border border-[#2A2D2A] rounded-md p-4 bg-[#181A18]" data-testid="dashboard-widget-jobs-throughput">
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500">Jobs Throughput — 6 Weeks</p>
                <div className="h-[86px] mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={CHART_DATA} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                      <XAxis dataKey="w" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip
                        cursor={{ fill: "#20241F" }}
                        contentStyle={{ background: "#101210", border: "1px solid #2A2D2A", borderRadius: 6, fontSize: 12, color: "#e4e4e7" }}
                        labelStyle={{ color: "#A7BFA0" }}
                      />
                      <Bar dataKey="jobs" fill="#739072" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-span-2 border border-[#3A2D2A] rounded-md p-4 bg-[#1E1815]" data-testid="dashboard-widget-management-attention">
                <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-amber-200/60">Management Attention</p>
                <p className="mt-2 font-heading font-bold text-2xl text-amber-100">5</p>
                <p className="mt-1 text-[11px] text-zinc-500">items surfaced for decision</p>
              </div>
              {WIDGETS.slice(6).map((w) => <Widget key={w.label} {...w} />)}
            </div>
          </div>
        </div>
        <p className="mt-4 text-[11px] text-zinc-500 tracking-wide" data-testid="dashboard-demo-disclaimer">
          Illustrative example only. All figures shown are fictional demonstration data, not a real business.
        </p>
      </FadeIn>
      <FadeIn delay={0.15} className="mt-14 max-w-3xl">
        <p className="text-base md:text-lg text-zinc-400 leading-relaxed">
          The goal is not to make management watch another piece of software all day.
        </p>
        <p className="mt-3 font-heading font-semibold text-white text-xl md:text-2xl leading-snug">
          The goal is for management to open one system and immediately know what matters.
        </p>
      </FadeIn>
    </div>
  </section>
);
